import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { INoticeBanner } from 'src/app/model/notice-banner/notice-banner';
import { BILLINGANDPAYMENT, CHATHISTORY, CLAIMSGROUP, CLAIMSHISTORYANDSTATUS, FILEACLAIM } from 'src/app/core/constants/feature-key.constants';
import { NavigationEnd, NavigationSkipped, Router } from '@angular/router';
import { ContractGuard } from 'src/app/core/guards/contract.guard';
import { ADPResourceService } from 'digital-core';
import { StorageService } from 'src/app/core/services/storage.service';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit, OnDestroy {
  isControllerDisabled = true;
  isExpired: boolean = false;
  isPTPO: boolean = false;
  isPrimary: boolean = true;
  isMobile: boolean = false;
  noticeBanner: INoticeBanner = null;
  isAccountClaimsEnabled: boolean = true;
  firstMenuTabSelectedText: string = "";
  isTechSupportHistoryEnabled: boolean = true;
  isActive: boolean = false;
  order = 'order';
  ascending = true;
  isNavLinksLoaded = false;
  navLinks = [{ order: 1, link: 'details', isActive: true, isVisible: true, label: 'Account_Nav_Summary_Text', icon: 'contract-details', iconActive: 'contract-details-blue' },
  { order: 2, link: 'profile', isActive: false, isVisible: true, label: 'Account_Nav_Profile_Text', icon: 'user', iconActive: 'user-blue' },
  { order: 3, link: 'change-password', isActive: false, isVisible: true, label: 'Account_Nav_ChangePassword_Text', icon: 'password-setting', iconActive: 'password-setting-blue' },
  { order: 4, link: 'payment-info', isActive: false, isVisible: true, label: 'Account_Nav_BillingPayment_Text', icon: 'billing-payment', iconActive: 'billing-payment-blue' },
  { order: 5, link: 'communication-preferences', isActive: false, isVisible: true, label: 'Account_Nav_CommunicationPreferences_Text', icon: 'messaging', iconActive: 'messaging' },
  { order: 6, link: 'family-management', isActive: false, isVisible: true, label: 'Account_Nav_ManageMembers_Text', icon: 'manage-members', iconActive: 'manage-members-blue' },
  { order: 7, link: 'tech-support-history', isActive: false, isVisible: true, label: 'Account_Nav_TechSupportHistory_Text', icon: 'tech-support-history', iconActive: 'tech-support-history-blue' },
  { order: 8, link: 'subscription', isActive: false, isVisible: true, label: 'Account_Nav_Subscription_Text', icon: 'subscription', iconActive: 'subscription-blue' }];
  // selectedMobileTab: { link: string; isActive: boolean; isVisible: boolean; label: string; icon: string; iconActive: string; };
  selectedMobileTab: any;
  isBillingAndPaymentEnabled: boolean = true;
  private destroy$ = new Subject<boolean>();
  public isNative: boolean;
  constructor(
    private loggedInUserSettings: LoggedInUserSettings,
    private router: Router,
    private contractGuard: ContractGuard,
    private resourceService: ADPResourceService,
    private storageService: StorageService
  ) {
    this.router.events.subscribe((ev) => {
      if(ev instanceof NavigationSkipped || ev instanceof NavigationEnd){
        this.selectedMobileTab = this.tabSelector();
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.isMobile = window.innerWidth < 992 ? true : false;
  }

  ngOnInit() {
    this.isMobile = window.innerWidth < 992 ? true : false;

    if(this.isMobile){
      this.firstMenuTabSelectedText = this.resourceService.getPortalLabel("Account_Nav_Summary_Text").Value;
    }

    this.isNavLinksLoaded = false;
    const joined$ = combineLatest(
      [
        this.loggedInUserSettings.$selectedContract,
      ]
    ).pipe(
      filter(([a]) => a !== null),
      takeUntil(this.destroy$)
    );

    joined$.subscribe(([selectedContract]: [ISelectedContract]) => {
      this.isActive = this.contractGuard.activateGuard(selectedContract);
      if (this.isActive) {
        this.isPrimary = selectedContract.isPrimary;
        this.isExpired = selectedContract.isExpired;
        this.isPTPO = selectedContract.isPTPO;
        this.isControllerDisabled = this.isExpired;
      }
      this.selectedMobileTab = this.tabSelector();

      let isSPOEEnabled = this.resourceService.isPortalSettingTrue("Global_SPOE_Enabled");

      // Secondary user
      if (!this.isPrimary) {

        // For TMO SPOE do not suppress the Account Summary / Details section from Protection 360 secondary user or P360 authorized user
        // For all other clients keep the existing logic of not allowing seocndary users to access Account Summary / Details
        if(!isSPOEEnabled){
          this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'details')[0]));
        }

        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'payment-info')[0]));
        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'family-management')[0]));
        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'communication-preferences')[0]));
        this.router.navigate(['account/', this.selectedMobileTab?.link]);
      }

      const dealerId = selectedContract.dealerId?.trim().toLowerCase();
      this.isBillingAndPaymentEnabled = this.loggedInUserSettings.isFeatureEnabled(selectedContract.dealerId, BILLINGANDPAYMENT);

      if (!this.isBillingAndPaymentEnabled || dealerId === 'lptp' || dealerId === 'dtepg') {
        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'payment-info')[0]));
      }

      this.isTechSupportHistoryEnabled = this.loggedInUserSettings.isFeatureEnabled(selectedContract.dealerId, CHATHISTORY);

      if (!this.isTechSupportHistoryEnabled) {
        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'tech-support-history')[0]));
      }

      const commPrefsEnabled = this.resourceService.isPortalSettingTrue('Account_Communication_Preferences_Enabled');
      if (!commPrefsEnabled) {
        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'communication-preferences')[0]));
      }

      const familyManagementEnabled = this.resourceService.isPortalSettingTrue('Account_FamilyManagement_Enabled');
      if (!familyManagementEnabled) {
        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'family-management')[0]));
      }

      const subscriptionEnabled = this.resourceService.isPortalSettingTrue('Account_Subscription_Enabled');
      if (!subscriptionEnabled) {
        this.navLinks = this.navLinks.filter(x => x !== (this.navLinks.filter(item => item.link === 'subscription')[0]));
      }

      const hasP360MobilePolicy = this.storageService.getLocal<string>(`EprismContractsCount`) > '0';
      const hasP360HomeTechPolicy = this.storageService.getLocal<string>(`ElitaContractsCount`) > '0';
      if (hasP360MobilePolicy && !hasP360HomeTechPolicy) {
        this.navLinks = this.navLinks.filter(x => !['communication-preferences', 'family-management'].includes(x.link));
      }

      this.isNavLinksLoaded = true;
    });
  }

  mobileTab(selectedtab) {
    this.selectedMobileTab = selectedtab?.value;
    this.router.navigate(['account/', selectedtab?.value?.link]);
  }

  tabSelector() {
    const tab = window?.location?.pathname;
    let navLink;

    let isSPOEEnabled = this.resourceService.isPortalSettingTrue("Global_SPOE_Enabled");

    if (tab?.includes('account/details')) {
      if(isSPOEEnabled){
        navLink = this.navLinks.filter(x => x.link === 'details')[0];
      }
      else if(this.isPrimary){
        navLink = this.navLinks.filter(x => x.link === 'details')[0];
      }
    }
    else if (tab?.includes('account/profile')) {
      navLink = this.navLinks.filter(x => x.link === 'profile')[0];
    }
    else if (tab?.includes('account/change-password')) {
      navLink = this.navLinks.filter(x => x.link === 'change-password')[0];
    }
    else if (tab?.includes('account/payment-info')) {
      navLink = this.navLinks.filter(x => x.link === 'payment-info')[0];
    }
    else if (tab?.includes('account/communication-preferences')) {
      navLink = this.navLinks.filter(x => x.link === 'communication-preferences')[0];
    }
    else if (tab?.includes('account/family-management')) {
      navLink = this.navLinks.filter(x => x.link === 'family-management')[0];
    }
    else if (tab?.includes('account/tech-support-history')) {
      navLink = this.navLinks.filter(x => x.link === 'tech-support-history')[0];
    }
    else if (tab?.includes('account/subscription')) {
      navLink = this.navLinks.filter(x => x.link === 'subscription')[0];
    }
    else {
      if(isSPOEEnabled){
        navLink = this.navLinks.filter(x => x.link === 'details')[0];
      }
      else{
        navLink = (this.isPrimary) ? this.navLinks.filter(x => x.link === 'details')[0] : this.navLinks.filter(x => x.link === 'profile')[0];
      }
    }
    this.selectedMobileTab = navLink;
    return navLink;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  getLabelValue(key) {
    let labelValue = this.resourceService.getLabel(key);
    if(labelValue == null){
      return this.firstMenuTabSelectedText;
    }
    return labelValue;
  }

}
